import "../../kendo.menu.js"; // Still required until kendo.ui is modularized
import { IKendoJQueryStatic, IKendoJQuery } from "../../core/index.js";
import { Widget } from "../../core/base/widget.js";
import { STYLES, DOT, REFERENCES } from "../constants.js";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

/**
 * MessageMenu provides context menu functionality for chat messages.
 * Handles actions like reply, copy, pin, and delete for individual messages.
 *
 * This is an internal collaborator that extends Widget for event handling.
 */
export class MessageMenu extends Widget {
    public static readonly options = {
        name: "ChatMessageMenu",
        filter: `${DOT}${STYLES.chatBubble}`,
        dataSource: [] as any[],
        keyboardAlignToAnchor: true
    };

    private menu: any;

    /**
     * Constructs a new MessageMenu instance.
     */
    constructor(element: IKendoJQuery, options: any) {
        options = $.extend(true, {}, MessageMenu.options, options);
        options.dataSource = MessageMenu.normalizeActions(options.dataSource || []);

        super(element, options);

        this.create();
        this.attachEvents();
    }

    /**
     * Creates the underlying Kendo UI ContextMenu.
     */
    private create(): void {
        this.menu = new kendo.ui.ContextMenu(this.element, this.options);
    }

    /**
     * Attaches event handlers to the context menu.
     */
    private attachEvents(): void {
        this.menu
            .bind("select", this.onSelect.bind(this))
            .bind("open", this.onOpen.bind(this))
            .bind("close", this.onClose.bind(this));
    }

    /**
     * Sets command attributes on menu items for identification.
     */
    private static normalizeActions(actions: any[]): any[] {
        return (actions || []).map((item: any) => ({
            ...item,
            attr: {
                ...(item.attr || {}),
                "data-command": item.name.toLowerCase()
            }
        }));
    }

    setActions(actions: any[]): void {
        const dataSource = MessageMenu.normalizeActions(actions);

        (this.options as any).dataSource = dataSource;
        this.menu.setOptions({ dataSource });
    }

    /**
     * Handles menu item selection.
     */
    private onSelect(e: any): void {
        const item = $(e.item);
        const message = $(e.target).closest(DOT + STYLES.message) as IKendoJQuery;
        const command = item.data("command");

        if (command) {
            this.executeCommand(command, item, message);
        }
    }

    /**
     * Handles menu open event.
     */
    onOpen(e: any): void {
        let setActive = true;
        const target = $(e.target);
        const originalTarget = $(e.event?.target).length ? $(e.event.target) : target;

        const isMessageRemoved = target.closest(DOT + STYLES.messageRemoved).length > 0;
        const hasTypingIndicator = target.find(DOT + STYLES.typingIndicator).length > 0;
        const isAttachmentMenuButton = originalTarget.closest(`[${REFERENCES.fileMenuButton}]`).length > 0;
        const message = target.closest(DOT + STYLES.message);

        if (isMessageRemoved || hasTypingIndicator || isAttachmentMenuButton || !message.length) {
            e.preventDefault();
            setActive = false;
        }

        target.toggleClass(STYLES.active, setActive);

        if (this.trigger("open", { message })) {
            e.preventDefault();
            target.removeClass(STYLES.active);
        }
    }

    /**
     * Handles menu close event.
     */
    private onClose(e: any): void {
        const target = $(e.target);
        const message = target.closest(DOT + STYLES.message);
        target.removeClass(STYLES.active);
        this.trigger("close", { message });
    }

    /**
     * Executes a command from the menu.
     */
    private executeCommand(command: string, item: IKendoJQuery, message: IKendoJQuery): void {
        this.trigger("execute", { type: command, item, message });
    }

    /**
     * Toggles the visibility of the delete menu item.
     */
    toggleDeleteVisibility(visible: boolean): void {
        const deleteItem = this.element.find("[data-command='delete']");
        if (deleteItem.length) {
            deleteItem.toggleClass(STYLES.hidden, !visible);
        }
    }

    destroy(): void {
        if (this.menu) {
            this.menu.destroy();
            this.menu = null;
            this.element.remove();
        }
    }
}
